import React from 'react';
import {
  Box,
  BoxProps,
  Theme,
  Typography,
  TypographyVariant,
} from '@mui/material';
import { useI18n } from '../../lib/hook/useI18n';

export enum ContentStatusType {
  Expired = 'EXPIRED',
}

export enum ContentStatusSizeType {
  Small = 'SMALL',
  Default = 'DEFAULT',
}

export interface ContentStatusTagType {
  size: ContentStatusSizeType;
  statusType: ContentStatusType | null;
  sx: BoxProps['sx'];
}

export interface ContentStatusProps {
  size: ContentStatusSizeType;
  statusType: ContentStatusType;
  sx: BoxProps['sx'];
}

type ContentStatusSizeConfig = {
  sx: BoxProps['sx'];
  variant: TypographyVariant;
};

interface ContentStatusTagConfigType {
  background: {
    [key in ContentStatusType]: string;
  };
  labelKey: { [key in ContentStatusType]: string };
  [ContentStatusSizeType.Small]: ContentStatusSizeConfig;
  [ContentStatusSizeType.Default]: ContentStatusSizeConfig;
}

const ContentStatusTagConfig: ContentStatusTagConfigType = {
  background: {
    [ContentStatusType.Expired]: 'var(--content-status-background-expired)',
  },
  labelKey: {
    [ContentStatusType.Expired]: 'content.status.tag.expired',
  },
  [ContentStatusSizeType.Small]: {
    sx: (theme) => ({ padding: theme.spacing(0.25, 0.5) }),
    variant: 'caption',
  },
  [ContentStatusSizeType.Default]: {
    sx: (theme) => ({ padding: theme.spacing(0.75, 1) }),
    variant: 'body2',
  },
};

const ContentStatusTag: React.FC<ContentStatusProps> = (
  props: ContentStatusProps
) => {
  const { t } = useI18n();
  const { statusType, sx: propSx, size } = props;
  const { sx: configSx, variant } = ContentStatusTagConfig[size];

  const processCustomStyle = ({
    customSx,
    theme,
  }: {
    customSx?: BoxProps['sx'];
    theme: Theme;
  }) => {
    if (customSx instanceof Function) {
      return customSx(theme);
    }
    return customSx || {};
  };

  return (
    <Box
      sx={(theme) =>
        ({
          ...processCustomStyle({ customSx: configSx, theme }),
          alignItems: 'center',
          backgroundColor: ContentStatusTagConfig.background[statusType],
          borderRadius: theme.spacing(0.5),
          display: 'flex',
          justifyContent: 'center',
          ...processCustomStyle({ customSx: propSx, theme }),
        } as any)
      }
    >
      <Typography color="textPrimary" variant={variant}>
        <Box component="strong">
          {t(ContentStatusTagConfig.labelKey[statusType])}
        </Box>
      </Typography>
    </Box>
  );
};

export default ContentStatusTag;
